import React, { useState, useEffect } from 'react';
import { API, useAuth } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Users, UserPlus, Mail, Shield, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const TeamPage = () => {
  const { user, token } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('recruiter');
  const [inviting, setInviting] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  const isAdmin = ['super_admin', 'client_admin'].includes(user?.role);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/team/members`, { 
          headers, 
          withCredentials: true 
        });
        setMembers(response.data.members || []);
      } catch (error) {
        console.error('Failed to fetch team:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, [token]);

  const handleInvite = async () => {
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }

    setInviting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/team/invite?email=${encodeURIComponent(inviteEmail)}&role=${inviteRole}`,
        {},
        { headers, withCredentials: true }
      );
      toast.success('Invitation sent successfully!');
      setInviteEmail('');
      setShowInviteDialog(false);
      
      // Refresh members
      const response = await axios.get(`${API}/team/members`, { headers, withCredentials: true });
      setMembers(response.data.members || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (memberId, newRole) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(
        `${API}/team/members/${memberId}/role?role=${newRole}`,
        {},
        { headers, withCredentials: true }
      );
      setMembers(prev => prev.map(m => 
        m.user_id === memberId ? { ...m, role: newRole } : m
      ));
      toast.success('Role updated');
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const getRoleBadge = (role) => {
    const config = {
      super_admin: { label: 'Super Admin', className: 'bg-red-100 text-red-800' },
      client_admin: { label: 'Admin', className: 'bg-purple-100 text-purple-800' },
      team_leader: { label: 'Team Lead', className: 'bg-blue-100 text-blue-800' },
      recruiter: { label: 'Recruiter', className: 'bg-green-100 text-green-800' },
      candidate: { label: 'Candidate', className: 'bg-slate-100 text-slate-800' },
    };
    const c = config[role] || config.candidate;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const recruiters = members.filter(m => ['recruiter', 'team_leader', 'client_admin'].includes(m.role));

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn" data-testid="team-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Team Management
            </h1>
            <p className="text-slate-600 mt-1">
              {recruiters.length} team {recruiters.length === 1 ? 'member' : 'members'}
            </p>
          </div>
          {isAdmin && (
            <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
              <DialogTrigger asChild>
                <Button data-testid="invite-member-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite Team Member
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Outfit' }}>Invite Team Member</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="colleague@company.com"
                      className="mt-1"
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select value={inviteRole} onValueChange={setInviteRole}>
                      <SelectTrigger className="mt-1" data-testid="invite-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="recruiter">Recruiter</SelectItem>
                        <SelectItem value="team_leader">Team Leader</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleInvite} disabled={inviting} data-testid="send-invite-btn">
                      {inviting ? 'Sending...' : 'Send Invitation'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Team Members Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : recruiters.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recruiters.map(member => (
              <Card key={member.user_id} className="card-hover" data-testid={`member-card-${member.user_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                        {member.picture ? (
                          <img src={member.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <Users className="w-6 h-6 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{member.name}</h3>
                        <p className="text-sm text-slate-500">{member.email}</p>
                      </div>
                    </div>
                    {isAdmin && member.user_id !== user?.user_id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleRoleChange(member.user_id, 'recruiter')}>
                            Set as Recruiter
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleRoleChange(member.user_id, 'team_leader')}>
                            Set as Team Leader
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    {getRoleBadge(member.role)}
                    {member.user_id === user?.user_id && (
                      <span className="text-xs text-slate-500">(You)</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No team members yet</h3>
              <p className="text-slate-500 mb-4">Start building your recruitment team</p>
              {isAdmin && (
                <Button onClick={() => setShowInviteDialog(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite First Member
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TeamPage;
